import { FC, useEffect, useRef } from 'react';
import { lazyRetry } from '@/utils/lazy-retry';
import {
	Navigate,
	Route,
	Routes,
	useLocation,
	useNavigate,
	useParams
} from 'react-router-dom';
import { useAuthContext } from '@/providers';
import { getFirstAccessiblePath } from '@/menu-items/first-accessible-path';
import { AuthState } from '@/providers/auth';
import { cleanupTrackedAssetsByRoute } from '@/store/assets/helpers';
import { AuthCallbackPage } from '../views/auth/callback';
import { NoAccessPage } from '../views/common/no-access';
import { AdministrationScopeGate, Loadable, PermissionGate, P } from '../components';
import { AdminRoute } from './admin';
import { AuthRoute } from './auth';

// AUTH ROUTES
const NotFound = Loadable(lazyRetry(() => import('../views/common/404-page')));

// AUTH ROUTES
const AuthPage = Loadable(lazyRetry(() => import('../views/auth')));
const ForgotPasswordPage = Loadable(
	lazyRetry(() => import('../views/auth/forgot-password'))
);
const CheckMailPage = Loadable(lazyRetry(() => import('../views/auth/check-mail')));

// PAGINI LEGALE (publice, linkuite din footer-ul paginilor de autentificare)
const TermsPage = Loadable(lazyRetry(() => import('../views/legal/terms')));
const PrivacyPolicyPage = Loadable(lazyRetry(() => import('../views/legal/privacy')));
const CookiePolicyPage = Loadable(lazyRetry(() => import('../views/legal/cookies')));

// paginile legale nu se salvează drept „currentPath" — după autentificare
// utilizatorul trebuie să ajungă la dashboard, nu înapoi pe o pagină publică
const LEGAL_PATHS = [
	'/termeni-si-conditii',
	'/politica-de-confidentialitate',
	'/politica-cookies'
];

// COMMON ROUTES
// PROJECTS
const ProiecteListPage = Loadable(
	lazyRetry(() => import('../views/common/proiecte/list-page'))
);
const ProiectEditorPage = Loadable(
	lazyRetry(() => import('../views/common/proiecte/editor-page'))
);
const ProiectDetaliuPage = Loadable(
	lazyRetry(() => import('../views/common/proiecte/detail-page'))
);
// NEIGHBORHOODS (cartiere)
const CartiereListPage = Loadable(
	lazyRetry(() => import('../views/common/cartiere/list-page'))
);
const CartierDetaliuPage = Loadable(
	lazyRetry(() => import('../views/common/cartiere/detail-page'))
);
const CartierEditorPage = Loadable(
	lazyRetry(() => import('../views/common/cartiere/editor-page'))
);
// USERS
const UsersManagementPage = Loadable(
	lazyRetry(() => import('../views/common/users/management'))
);
// MODERATORI (web users hardcodat pe moderator — grupul Organizație)
const ModeratorsListPage = Loadable(
	lazyRetry(() => import('../views/common/users/moderators/list'))
);
const CreateModeratorPage = Loadable(
	lazyRetry(() => import('../views/common/users/moderators/create'))
);
// ROLES & PERMISSIONS
const RolesPermissionsPage = Loadable(
	lazyRetry(() => import('../views/common/roles-permissions/management'))
);
const CreateUserPage = Loadable(
	lazyRetry(() => import('../views/common/users/create-user'))
);
const CitizensListPage = Loadable(
	lazyRetry(() => import('../views/common/users/app-users/all-users'))
);
const UserProfilePage = Loadable(
	lazyRetry(() => import('../views/common/users/app-users/user-info'))
);
// WEB USERS (interfață unică detaliu + editare, în funcție de rol)
const WebUserDetailsPage = Loadable(
	lazyRetry(() => import('../views/common/users/web-user-details'))
);
// INVITAȚII (mock)
const InvitationsPage = Loadable(lazyRetry(() => import('../views/common/invitations')));
const InvitationDetailsPage = Loadable(
	lazyRetry(() => import('../views/common/invitations/details'))
);
// FSP
const AllFspsPage = Loadable(
	lazyRetry(() => import('../views/common/users/fsp-users/all-fsp'))
);
const FspInfoPage = Loadable(
	lazyRetry(() => import('../views/common/users/fsp-users/fsp-info'))
);
const CreateFSPPage = Loadable(
	lazyRetry(() => import('../views/common/users/fsp-users/create-fsp'))
);
const EditFSPPage = Loadable(
	lazyRetry(() => import('../views/common/users/fsp-users/edit-fsp'))
);
// ANNOUNCEMENTS
const CreateAnnouncementPage = Loadable(
	lazyRetry(() => import('../views/common/announcment/create'))
);
const EditAnnouncementPage = Loadable(
	lazyRetry(() => import('../views/common/announcment/edit'))
);
const AnnouncementDetailsPage = Loadable(
	lazyRetry(() => import('../views/common/announcment/details'))
);
// CONSULTATIONS
const ConsultationDetails = Loadable(
	lazyRetry(() => import('../views/common/consultation/details'))
);
const CreateConsultationPage = Loadable(
	lazyRetry(() => import('../views/common/consultation/create'))
);
const EditConsultationPage = Loadable(
	lazyRetry(() => import('../views/common/consultation/edit-survey'))
);

// AGREMENTS
const AllPP = Loadable(lazyRetry(() => import('../views/common/clause/pp/all')));
const CreatePP = Loadable(lazyRetry(() => import('../views/common/clause/pp/create')));
const DetailsPP = Loadable(lazyRetry(() => import('../views/common/clause/pp/details')));
const EditPP = Loadable(lazyRetry(() => import('../views/common/clause/pp/edit')));

const AllTC = Loadable(lazyRetry(() => import('../views/common/clause/tc/all')));
const CreateTC = Loadable(lazyRetry(() => import('../views/common/clause/tc/create')));
const DetailsTC = Loadable(lazyRetry(() => import('../views/common/clause/tc/details')));
const EditTC = Loadable(lazyRetry(() => import('../views/common/clause/tc/edit')));

const AllGDPR = Loadable(lazyRetry(() => import('../views/common/clause/gdpr/all')));
const CreateGDPR = Loadable(
	lazyRetry(() => import('../views/common/clause/gdpr/create'))
);
const DetailsGDPR = Loadable(
	lazyRetry(() => import('../views/common/clause/gdpr/details'))
);
const EditGDPR = Loadable(lazyRetry(() => import('../views/common/clause/gdpr/edit')));

const TypesPage = Loadable(lazyRetry(() => import('../views/common/project-types')));
const StatusesPage = Loadable(
	lazyRetry(() => import('../views/common/project-statuses'))
);
const JudetePage = Loadable(lazyRetry(() => import('../views/common/judete')));
const UatPage = Loadable(lazyRetry(() => import('../views/common/uat')));
const GestionareTextePage = Loadable(
	lazyRetry(() => import('../views/common/text-settings'))
);
const AnnouncementTypesPage = Loadable(
	lazyRetry(() => import('../views/common/announcement-types'))
);
const GeometryStylesPage = Loadable(
	lazyRetry(() => import('../views/common/geometry-styles'))
);
const MapTilesPage = Loadable(lazyRetry(() => import('../views/common/map-tiles')));
const SettingsPage = Loadable(lazyRetry(() => import('../views/common/settings')));
const GeometryStyleEditor = Loadable(
	lazyRetry(() =>
		import('../views/common/geometry-styles/StilEditor').then((m) => ({
			default: m.StilEditor
		}))
	)
);

// Profile page
const ProfilePage = Loadable(lazyRetry(() => import('../views/common/profile')));

// Feedback
const FeedbackBugsPage = Loadable(
	lazyRetry(() => import('../views/common/feedback/bug/all'))
);
const FeedbackComplimentPage = Loadable(
	lazyRetry(() => import('../views/common/feedback/compliment/all'))
);
const FeedbackSuggestionPage = Loadable(
	lazyRetry(() => import('../views/common/feedback/suggestion/all'))
);
const FeedbackSupportPage = Loadable(
	lazyRetry(() => import('../views/common/feedback/support/all'))
);

const DetailsFeedbackBugPage = Loadable(
	lazyRetry(() => import('../views/common/feedback/bug/details'))
);
const DetailsFeedbackComplimentPage = Loadable(
	lazyRetry(() => import('../views/common/feedback/compliment/details'))
);
const DetailsFeedbackSuggestionPage = Loadable(
	lazyRetry(() => import('../views/common/feedback/suggestion/details'))
);
const DetailsFeedbackSupportPage = Loadable(
	lazyRetry(() => import('../views/common/feedback/support/details'))
);

// Version interface
const VersionPage = Loadable(lazyRetry(() => import('../views/common/version/all')));
const VersionPageDetails = Loadable(
	lazyRetry(() => import('../views/common/version/details'))
);

// CHAT (Chat Core SDK — pagină drop-in ChatKitFullPage)
const ChatPage = Loadable(lazyRetry(() => import('../views/common/chat')));

const LegacyAdministrationDetailsRedirect: FC = () => {
	const { id } = useParams<{ id: string }>();

	return (
		<Navigate
			to={id ? `/common/administrations/${id}` : '/common/administrations'}
			replace
		/>
	);
};

const LegacyAdministrationEditRedirect: FC = () => {
	const { id } = useParams<{ id: string }>();

	return (
		<Navigate
			to={id ? `/common/administrations/edit/${id}` : '/common/administrations'}
			replace
		/>
	);
};

const InitialRedirect: FC = () => {
	const { user, authState } = useAuthContext();
	const navigate = useNavigate();

	useEffect(() => {
		if (authState === AuthState.Loading) return;
		const savedPath = localStorage.getItem('currentPath');
		if (user?.role) {
			if (savedPath && savedPath !== '/') {
				navigate(savedPath, { replace: true });
			} else {
				navigate(getFirstAccessiblePath(user), { replace: true });
			}
		} else {
			navigate('/auth', { replace: true });
		}
	}, [user, navigate, authState]);

	return null;
};

export const RoutesContainer: FC = () => {
	const { authState } = useAuthContext();
	const location = useLocation();
	const previousPathRef = useRef(location.pathname);

	useEffect(() => {
		const previousPath = previousPathRef.current;
		if (previousPath && previousPath !== location.pathname) {
			// eslint-disable-next-line no-void
			void cleanupTrackedAssetsByRoute(previousPath);
		}
		previousPathRef.current = location.pathname;

		const { pathname, search } = location;
		if (
			!pathname.startsWith('/auth') &&
			!LEGAL_PATHS.some((path) => pathname.startsWith(path))
		) {
			localStorage.setItem('currentPath', `${pathname}${search}`);
		}
	}, [location]);

	// Expirarea sesiunii (401 în zbor / inactivitate) e tratată centralizat de AuthProvider,
	// care ridică modala de expirare cu CTA spre re-autentificare — nu mai redirectăm abrupt
	// din rute (evită eroarea brută și flash-ul spre /auth).

	if (authState === AuthState.NoAccess) return <NoAccessPage />;

	return (
		<Routes>
			<Route path="/" element={<InitialRedirect />} />
			<Route path="/auth/callback" element={<AuthCallbackPage />} />
			<Route path="/" element={<AuthRoute redirectPath="/common/project" />}>
				<Route path="/auth" element={<AuthPage />} />
				<Route path="/auth/forgot-password" element={<ForgotPasswordPage />} />
				<Route path="/auth/check-mail" element={<CheckMailPage />} />
			</Route>
			{/* pagini legale — publice, accesibile autentificat și neautentificat */}
			<Route path="/termeni-si-conditii" element={<TermsPage />} />
			<Route path="/politica-de-confidentialitate" element={<PrivacyPolicyPage />} />
			<Route path="/politica-cookies" element={<CookiePolicyPage />} />
			<Route path="/common" element={<AdminRoute redirectPath="/" />}>
				{/* CONSULTATIONS — doar detaliu/creare/editare: lista dedicată nu mai există,
				    o consultare aparține unui proiect sau unui cartier și se deschide din el */}
				<Route
					path="/common/consultation/:id"
					element={
						<PermissionGate permission={P.surveysRead}>
							<ConsultationDetails />
						</PermissionGate>
					}
				/>
				<Route
					path="/common/consultation/create"
					element={
						<PermissionGate permission={P.surveysWrite}>
							<CreateConsultationPage />
						</PermissionGate>
					}
				/>
				<Route
					path="/common/consultation/edit/:id"
					element={
						<PermissionGate permission={P.surveysWrite}>
							<EditConsultationPage />
						</PermissionGate>
					}
				/>
				{/* PROJECTS */}
				<Route
					path="/common/project"
					element={
						<PermissionGate permission={P.projectsRead}>
							<ProiecteListPage />
						</PermissionGate>
					}
				/>
				<Route
					path="/common/project/create"
					element={
						<PermissionGate permission={P.projectsWrite}>
							<ProiectEditorPage />
						</PermissionGate>
					}
				/>
				<Route
					path="/common/project/:id/edit"
					element={
						<PermissionGate permission={P.projectsWrite}>
							<ProiectEditorPage />
						</PermissionGate>
					}
				/>
				<Route
					path="/common/project/:id"
					element={
						<PermissionGate permission={P.projectsRead}>
							<ProiectDetaliuPage />
						</PermissionGate>
					}
				/>
				{/* NEIGHBORHOODS (cartiere) */}
				<Route
					path="/common/cartiere"
					element={
						<PermissionGate permission={P.neighborhoodsRead}>
							<CartiereListPage />
						</PermissionGate>
					}
				/>
				<Route
					path="/common/cartiere/create"
					element={
						<PermissionGate permission={P.neighborhoodsWrite}>
							<CartierEditorPage />
						</PermissionGate>
					}
				/>
				<Route
					path="/common/cartiere/:id/edit"
					element={
						<PermissionGate permission={P.neighborhoodsWrite}>
							<CartierEditorPage />
						</PermissionGate>
					}
				/>
				<Route
					path="/common/cartiere/:id"
					element={
						<PermissionGate permission={P.neighborhoodsRead}>
							<CartierDetaliuPage />
						</PermissionGate>
					}
				/>
				{/* USERS */}
				<Route
					path="/common/users"
					element={
						<PermissionGate permission={P.usersRead}>
							<UsersManagementPage />
						</PermissionGate>
					}
				/>
				<Route
					path="/common/users/create"
					element={
						<PermissionGate permission={P.usersWrite}>
							<CreateUserPage />
						</PermissionGate>
					}
				/>
				{/* MODERATORI (hardcodat pe moderator) — grupul Organizație */}
				<Route
					path="/common/moderators"
					element={
						<PermissionGate permission={P.usersRead}>
							<ModeratorsListPage />
						</PermissionGate>
					}
				/>
				<Route
					path="/common/moderators/create"
					element={
						<PermissionGate permission={P.usersWrite}>
							<CreateModeratorPage />
						</PermissionGate>
					}
				/>
				{/* ROLES & PERMISSIONS */}
				<Route
					path="/common/roles-permissions"
					element={
						<PermissionGate permission={[P.rolesRead, P.permissionsRead]}>
							<RolesPermissionsPage />
						</PermissionGate>
					}
				/>
				{/* Detaliu unic pentru orice utilizator web */}
				<Route
					path="/common/users/view/:id"
					element={
						<PermissionGate permission={P.usersRead}>
							<WebUserDetailsPage />
						</PermissionGate>
					}
				/>
				<Route
					path="/common/users-app"
					element={
						<PermissionGate permission={P.usersRead}>
							<CitizensListPage />
						</PermissionGate>
					}
				/>
				<Route
					path="/common/users-app/:id"
					element={
						<PermissionGate permission={P.usersRead}>
							<UserProfilePage />
						</PermissionGate>
					}
				/>
				{/* INVITAȚII */}
				<Route
					path="/common/invitations"
					element={
						<PermissionGate permission={P.invitationsRead}>
							<InvitationsPage />
						</PermissionGate>
					}
				/>
				<Route
					path="/common/invitations/:id"
					element={
						<PermissionGate permission={P.invitationsRead}>
							<InvitationDetailsPage />
						</PermissionGate>
					}
				/>
				{/* ADMINS */}
				<Route
					path="/common/users-admin"
					element={<Navigate to="/common/users?roleId=2" replace />}
				/>
				<Route
					path="/common/users-admin/:id"
					element={
						<PermissionGate permission={P.usersRead}>
							<WebUserDetailsPage />
						</PermissionGate>
					}
				/>
				<Route
					path="/common/users-admin/create"
					element={<Navigate to="/common/users/create?roleId=2" replace />}
				/>
				{/* MODERATOR */}
				{/* Moderatorii au secțiune dedicată (grupul Organizație) — aliasurile vechi redirectează acolo. */}
				<Route
					path="/common/users-moderator"
					element={<Navigate to="/common/moderators" replace />}
				/>
				<Route
					path="/common/users-moderator/:id"
					element={
						<PermissionGate permission={P.usersRead}>
							<WebUserDetailsPage />
						</PermissionGate>
					}
				/>
				<Route
					path="/common/users-moderator/create"
					element={<Navigate to="/common/moderators/create" replace />}
				/>
				{/* FSP */}
				<Route
					path="/common/administrations"
					element={
						<PermissionGate permission={P.administrationsRead}>
							<AllFspsPage />
						</PermissionGate>
					}
				/>
				<Route
					path="/common/administrations/:id"
					element={
						<PermissionGate permission={P.administrationsRead}>
							<AdministrationScopeGate>
								<FspInfoPage />
							</AdministrationScopeGate>
						</PermissionGate>
					}
				/>
				<Route
					path="/common/administrations/create"
					element={
						<PermissionGate permission={P.administrationsWrite}>
							<AdministrationScopeGate>
								<CreateFSPPage />
							</AdministrationScopeGate>
						</PermissionGate>
					}
				/>
				<Route
					path="/common/administrations/edit/:id"
					element={
						<PermissionGate permission={P.administrationsWrite}>
							<AdministrationScopeGate>
								<EditFSPPage />
							</AdministrationScopeGate>
						</PermissionGate>
					}
				/>
				<Route
					path="/common/users-fsp"
					element={<Navigate to="/common/administrations" replace />}
				/>
				<Route
					path="/common/users-fsp/create"
					element={<Navigate to="/common/administrations/create" replace />}
				/>
				<Route
					path="/common/users-fsp/edit/:id"
					element={<LegacyAdministrationEditRedirect />}
				/>
				<Route
					path="/common/users-fsp/:id"
					element={<LegacyAdministrationDetailsRedirect />}
				/>
				{/* ANNOUNCEMENTS — doar detaliu/creare/editare (vezi nota de la consultări) */}
				<Route
					path="/common/announcment/:id"
					element={
						<PermissionGate permission={P.announcementsRead}>
							<AnnouncementDetailsPage />
						</PermissionGate>
					}
				/>
				<Route
					path="/common/announcment/create"
					element={
						<PermissionGate permission={P.announcementsWrite}>
							<CreateAnnouncementPage />
						</PermissionGate>
					}
				/>
				<Route
					path="/common/announcment/edit/:id"
					element={
						<PermissionGate permission={P.announcementsWrite}>
							<EditAnnouncementPage />
						</PermissionGate>
					}
				/>
				{/* AGREMENTS */}
				<Route
					path="/common/clause/pp"
					element={
						<PermissionGate permission={P.agreementsRead}>
							<AllPP />
						</PermissionGate>
					}
				/>
				<Route
					path="/common/clause/pp/create"
					element={
						<PermissionGate permission={P.agreementsWrite}>
							<CreatePP />
						</PermissionGate>
					}
				/>
				<Route
					path="/common/clause/pp/:id"
					element={
						<PermissionGate permission={P.agreementsRead}>
							<DetailsPP />
						</PermissionGate>
					}
				/>
				<Route
					path="/common/clause/pp/edit/:id"
					element={
						<PermissionGate permission={P.agreementsWrite}>
							<EditPP />
						</PermissionGate>
					}
				/>

				<Route
					path="/common/clause/tc"
					element={
						<PermissionGate permission={P.agreementsRead}>
							<AllTC />
						</PermissionGate>
					}
				/>
				<Route
					path="/common/clause/tc/create"
					element={
						<PermissionGate permission={P.agreementsWrite}>
							<CreateTC />
						</PermissionGate>
					}
				/>
				<Route
					path="/common/clause/tc/:id"
					element={
						<PermissionGate permission={P.agreementsRead}>
							<DetailsTC />
						</PermissionGate>
					}
				/>
				<Route
					path="/common/clause/tc/edit/:id"
					element={
						<PermissionGate permission={P.agreementsWrite}>
							<EditTC />
						</PermissionGate>
					}
				/>

				<Route
					path="/common/clause/gdpr"
					element={
						<PermissionGate permission={P.agreementsRead}>
							<AllGDPR />
						</PermissionGate>
					}
				/>
				<Route
					path="/common/clause/gdpr/create"
					element={
						<PermissionGate permission={P.agreementsWrite}>
							<CreateGDPR />
						</PermissionGate>
					}
				/>
				<Route
					path="/common/clause/gdpr/:id"
					element={
						<PermissionGate permission={P.agreementsRead}>
							<DetailsGDPR />
						</PermissionGate>
					}
				/>
				<Route
					path="/common/clause/gdpr/edit/:id"
					element={
						<PermissionGate permission={P.agreementsWrite}>
							<EditGDPR />
						</PermissionGate>
					}
				/>

				<Route
					path="/common/status-proiect"
					element={
						<PermissionGate permission={P.projectStagesWrite}>
							<StatusesPage />
						</PermissionGate>
					}
				/>
				{/* Județe / UAT — gate pe counties:read / uats:read (acces direct pe URL → modal Forbidden). */}
				<Route
					path="/common/judete"
					element={
						<PermissionGate permission={P.countiesRead}>
							<JudetePage />
						</PermissionGate>
					}
				/>
				<Route
					path="/common/uat"
					element={
						<PermissionGate permission={P.uatsRead}>
							<UatPage />
						</PermissionGate>
					}
				/>
				{/* GESTIONARE TEXTE — gate pe translations:read (acces direct pe URL → modal Forbidden). */}
				<Route
					path="/common/gestionare-texte"
					element={<Navigate to="/common/gestionare-texte/admin" replace />}
				/>
				<Route
					path="/common/gestionare-texte/:target"
					element={
						<PermissionGate permission={P.translationsRead}>
							<GestionareTextePage />
						</PermissionGate>
					}
				/>
				<Route
					path="/common/tipuri-anunt"
					element={
						<PermissionGate permission={P.announcementTypesWrite}>
							<AnnouncementTypesPage />
						</PermissionGate>
					}
				/>
				<Route
					path="/common/stiluri-geometrie"
					element={
						<PermissionGate permission={P.geometryStylesRead}>
							<GeometryStylesPage />
						</PermissionGate>
					}
				/>
				<Route
					path="/common/stiluri-geometrie/nou"
					element={
						<PermissionGate permission={P.geometryStylesWrite}>
							<GeometryStyleEditor />
						</PermissionGate>
					}
				/>
				<Route
					path="/common/stiluri-geometrie/:id/editeaza"
					element={
						<PermissionGate permission={P.geometryStylesWrite}>
							<GeometryStyleEditor />
						</PermissionGate>
					}
				/>
				<Route
					path="/common/tile-uri"
					element={
						<PermissionGate permission={P.mapTilesWrite}>
							<MapTilesPage />
						</PermissionGate>
					}
				/>
				<Route
					path="/common/types"
					element={
						<PermissionGate permission={P.projectTypesRead}>
							<TypesPage />
						</PermissionGate>
					}
				/>

				{/* Profile page */}
				<Route path="/common/profile" element={<ProfilePage />} />
				{/* CHAT (mediat de BE: api/chat/*) — gate pe permisiunea `chat`. */}
				<Route
					path="/common/chat"
					element={
						<PermissionGate permission={P.chat}>
							<ChatPage />
						</PermissionGate>
					}
				/>
				{/* feedback pages */}
				<Route
					path="/common/feedback/bug"
					element={
						<PermissionGate permission={P.feedbacksRead}>
							<FeedbackBugsPage />
						</PermissionGate>
					}
				/>
				<Route
					path="/common/feedback/compliment"
					element={
						<PermissionGate permission={P.feedbacksRead}>
							<FeedbackComplimentPage />
						</PermissionGate>
					}
				/>
				<Route
					path="/common/feedback/suggestion"
					element={
						<PermissionGate permission={P.feedbacksRead}>
							<FeedbackSuggestionPage />
						</PermissionGate>
					}
				/>
				<Route
					path="/common/feedback/support"
					element={
						<PermissionGate permission={P.feedbacksRead}>
							<FeedbackSupportPage />
						</PermissionGate>
					}
				/>

				<Route
					path="/common/feedback/bug/:id"
					element={
						<PermissionGate permission={P.feedbacksRead}>
							<DetailsFeedbackBugPage />
						</PermissionGate>
					}
				/>
				<Route
					path="/common/feedback/compliment/:id"
					element={
						<PermissionGate permission={P.feedbacksRead}>
							<DetailsFeedbackComplimentPage />
						</PermissionGate>
					}
				/>
				<Route
					path="/common/feedback/suggestion/:id"
					element={
						<PermissionGate permission={P.feedbacksRead}>
							<DetailsFeedbackSuggestionPage />
						</PermissionGate>
					}
				/>
				<Route
					path="/common/feedback/support/:id"
					element={
						<PermissionGate permission={P.feedbacksRead}>
							<DetailsFeedbackSupportPage />
						</PermissionGate>
					}
				/>
				{/* version pages */}
				<Route
					path="/common/version"
					element={
						<PermissionGate permission={P.mobileReleasesRead}>
							<VersionPage />
						</PermissionGate>
					}
				/>
				<Route
					path="/common/version/:id"
					element={
						<PermissionGate permission={P.mobileReleasesRead}>
							<VersionPageDetails />
						</PermissionGate>
					}
				/>
				{/* SETĂRI (superadmin) — gate pe `settings:manage` (cheie sintetică; doar superadmin
						trece prin bypass). BE-ul gate-uiește managementul pe rolul Superadmin. */}
				<Route
					path="/common/settings"
					element={
						<PermissionGate permission={P.settingsManage}>
							<SettingsPage />
						</PermissionGate>
					}
				/>
			</Route>
			<Route path="*" element={<NotFound />} />
		</Routes>
	);
};
